// AABB entity physics (ENG-16..18). Collision resolves the entity box against the
// per-cell sub-boxes returned by `boxesAt` (ENG-18) — full cubes, slabs, stairs,
// snow layers. The feel matches the §13 per-tick Minecraft constants. Swimming
// (§5.3) applies buoyancy + water drag when the body is in a fluid.
import { PLAYER, TICK, WATER } from '../config';
import type { Box } from '../shapes';

/** Local collision boxes for the block at (x,y,z); empty = passable. */
export type BoxFn = (x: number, y: number, z: number) => readonly Box[];
export type SolidFn = (x: number, y: number, z: number) => boolean;

export const WALK_PER_TICK = PLAYER.WALK / TICK.TPS;
export const SPRINT_PER_TICK = PLAYER.SPRINT / TICK.TPS;
export const SNEAK_PER_TICK = PLAYER.SNEAK / TICK.TPS;
export const GRAVITY = -PLAYER.GRAVITY; // magnitude 0.08 applied downward
export const DRAG = PLAYER.DRAG; // 0.98
export const JUMP_V = PLAYER.JUMP_V; // 0.45
export const AIR_CONTROL = PLAYER.AIR_CONTROL; // 0.3
export const FLY_PER_TICK = PLAYER.FLY / TICK.TPS; // creative/spectator flight
export const PLAYER_HALF = PLAYER.AABB_WIDTH / 2;
export const PLAYER_HEIGHT = PLAYER.AABB_HEIGHT;

const EPS = 1e-4;
const FULL_CUBE: readonly Box[] = [[0, 0, 0, 1, 1, 1]];
const NONE: readonly Box[] = [];

/** Adapt a boolean solidity function to full-cube boxes (tests / raycast). */
export function boxesFromSolid(isSolid: SolidFn): BoxFn {
  return (x, y, z) => (isSolid(x, y, z) ? FULL_CUBE : NONE);
}

export interface Body {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
  /** Creative flight active (toggled by double-space; ignored unless caps.fly). */
  flying?: boolean;
}

export interface MoveInput {
  forward?: boolean;
  back?: boolean;
  left?: boolean;
  right?: boolean;
  jump?: boolean;
  sprint?: boolean;
  sneak?: boolean;
}

export function makeBody(x: number, y: number, z: number): Body {
  return { x, y, z, vx: 0, vy: 0, vz: 0, onGround: false, flying: false };
}

/** Flight/no-clip switches for creative & spectator movement (M4.4). */
export interface StepOpts {
  /** Creative flight: no gravity, jump/sneak = up/down, still collides. */
  flying?: boolean;
  /** Spectator: free movement through all blocks, no collision. */
  noClip?: boolean;
  /** Movement-speed multiplier from status effects (speed/slowness, M12.2). */
  speedMul?: number;
  /** Extra jump velocity from Jump Boost (M12.2). */
  jumpBoost?: number;
}

export function blockIntersectsBody(body: Body, x: number, y: number, z: number, half = PLAYER_HALF, height = PLAYER_HEIGHT): boolean {
  return (
    x + 1 > body.x - half && x < body.x + half &&
    y + 1 > body.y && y < body.y + height &&
    z + 1 > body.z - half && z < body.z + half
  );
}

/** Move along one axis, then resolve against the tightest overlapping box. */
function moveAxis(body: Body, half: number, height: number, axis: 0 | 1 | 2, vel: number, boxesAt: BoxFn): void {
  if (axis === 0) body.x += vel;
  else if (axis === 1) body.y += vel;
  else body.z += vel;

  const eMinX = body.x - half, eMaxX = body.x + half;
  const eMinY = body.y, eMaxY = body.y + height;
  const eMinZ = body.z - half, eMaxZ = body.z + half;
  const cx0 = Math.floor(eMinX), cx1 = Math.floor(eMaxX - EPS);
  const cy0 = Math.floor(eMinY), cy1 = Math.floor(eMaxY - EPS);
  const cz0 = Math.floor(eMinZ), cz1 = Math.floor(eMaxZ - EPS);

  let hit = false;
  let clamp = vel > 0 ? Infinity : -Infinity;
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const boxes = boxesAt(cx, cy, cz);
        for (const b of boxes) {
          const bMinX = cx + b[0], bMinY = cy + b[1], bMinZ = cz + b[2];
          const bMaxX = cx + b[3], bMaxY = cy + b[4], bMaxZ = cz + b[5];
          if (eMaxX > bMinX && eMinX < bMaxX && eMaxY > bMinY && eMinY < bMaxY && eMaxZ > bMinZ && eMinZ < bMaxZ) {
            hit = true;
            if (axis === 0) clamp = vel > 0 ? Math.min(clamp, bMinX) : Math.max(clamp, bMaxX);
            else if (axis === 1) clamp = vel > 0 ? Math.min(clamp, bMinY) : Math.max(clamp, bMaxY);
            else clamp = vel > 0 ? Math.min(clamp, bMinZ) : Math.max(clamp, bMaxZ);
          }
        }
      }
    }
  }

  if (!hit) return;
  if (axis === 0) {
    body.x = vel > 0 ? clamp - half - EPS : clamp + half + EPS;
    body.vx = 0;
  } else if (axis === 1) {
    if (vel > 0) {
      body.y = clamp - height - EPS;
    } else {
      body.y = clamp + EPS;
      body.onGround = true;
    }
    body.vy = 0;
  } else {
    body.z = vel > 0 ? clamp - half - EPS : clamp + half + EPS;
    body.vz = 0;
  }
}

export function collideAndMove(body: Body, boxesAt: BoxFn, half: number = PLAYER_HALF, height: number = PLAYER_HEIGHT): void {
  body.onGround = false;
  moveAxis(body, half, height, 0, body.vx, boxesAt);
  moveAxis(body, half, height, 1, body.vy, boxesAt);
  moveAxis(body, half, height, 2, body.vz, boxesAt);
}

/** Advance a player one tick. `inFluid` switches to buoyant swim physics (§5.3);
 *  `opts` enables creative flight / spectator no-clip (M4.4). */
export function stepPlayer(body: Body, input: MoveInput, yaw: number, boxesAt: BoxFn, inFluid = false, opts: StepOpts = {}): void {
  const fwd = (input.forward ? 1 : 0) - (input.back ? 1 : 0);
  const strafe = (input.right ? 1 : 0) - (input.left ? 1 : 0);
  let dx = -Math.sin(yaw) * fwd + Math.cos(yaw) * strafe;
  let dz = -Math.cos(yaw) * fwd - Math.sin(yaw) * strafe;
  const len = Math.hypot(dx, dz);
  if (len > 0) { dx /= len; dz /= len; }
  const speed = (input.sprint ? SPRINT_PER_TICK : input.sneak ? SNEAK_PER_TICK : WALK_PER_TICK) * (opts.speedMul ?? 1);

  // Spectator no-clip: free flight through everything (no collision, no gravity).
  if (opts.noClip) {
    const fly = FLY_PER_TICK * (input.sprint ? 2 : 1);
    body.vx = dx * fly;
    body.vz = dz * fly;
    body.vy = ((input.jump ? 1 : 0) - (input.sneak ? 1 : 0)) * fly;
    body.x += body.vx;
    body.y += body.vy;
    body.z += body.vz;
    body.onGround = false;
    return;
  }

  // Creative flight: hover (no gravity), jump/sneak climb/descend, but collide.
  if (opts.flying) {
    const fly = FLY_PER_TICK * (input.sprint ? 2 : 1);
    body.vx = dx * fly;
    body.vz = dz * fly;
    body.vy = ((input.jump ? 1 : 0) - (input.sneak ? 1 : 0)) * fly;
    collideAndMove(body, boxesAt);
    return;
  }

  if (inFluid && !body.onGround) {
    // Swim: accelerate toward input, capped; water drags all motion; buoyancy
    // keeps the fall gentle; holding jump swims up. Only while not grounded —
    // wading in shallow shoreline water with your feet on solid ground should
    // jump like normal (below), not creep up at WATER.SWIM_UP's weak rate,
    // which never clears a 1-block ledge out of the water.
    body.vx += dx * speed * 0.28;
    body.vz += dz * speed * 0.28;
    const h = Math.hypot(body.vx, body.vz);
    const cap = speed * 0.9;
    if (h > cap) { const k = cap / h; body.vx *= k; body.vz *= k; }
    body.vx *= WATER.H_DRAG;
    body.vz *= WATER.H_DRAG;
    if (input.jump) body.vy += WATER.SWIM_UP;
    body.onGround = false;
    collideAndMove(body, boxesAt);
    body.vy = (body.vy - WATER.GRAVITY) * WATER.V_DRAG;
    if (body.vy < -0.4) body.vy = -0.4;
    return;
  }

  if (body.onGround) {
    body.vx = dx * speed;
    body.vz = dz * speed;
  } else if (len > 0) {
    // Air-strafe (§13): accelerate toward the input by a fixed impulse, but only
    // clamp the component *along* the input to walk speed — momentum carried in
    // perpendicular to the press is retained (the Minecraft bunny-hop feel),
    // rather than the whole velocity being capped and strafe momentum discarded.
    const accel = speed * AIR_CONTROL;
    body.vx += dx * accel;
    body.vz += dz * accel;
    const along = body.vx * dx + body.vz * dz; // dx,dz already unit-length
    if (along > speed) {
      const excess = along - speed;
      body.vx -= dx * excess;
      body.vz -= dz * excess;
    }
  }

  if (input.jump && body.onGround) {
    body.vy = JUMP_V + (opts.jumpBoost ?? 0);
    body.onGround = false;
  }

  collideAndMove(body, boxesAt);
  body.vy = (body.vy - GRAVITY) * DRAG;
}
